import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Loader2, MoreVertical, Shield, Trash2, UserCheck, UserX } from 'lucide-react';
import type { OrgMember } from '@examing/types';
import Avatar from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { StatusBadge } from 'components/ui/status-badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from 'components/ui/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from 'components/ui/dropdown-menu';
import { cn } from 'lib/utils';
import {
  ASSIGNABLE_ROLES,
  STATUS_TONE,
  lastActiveLabel,
  memberStatus,
  roleLabel,
  statusLabel,
  type MemberActions,
} from './memberLabels';

/** Row/menu link to a member: route link normally, callback in dialog mode. */
function MemberLink({
  memberId,
  onSelectMember,
  className,
  children,
}: {
  memberId: string;
  onSelectMember?: (id: string) => void;
  className?: string;
  children: React.ReactNode;
}) {
  return onSelectMember ? (
    <button type="button" onClick={() => onSelectMember(memberId)} className={cn(className, 'text-left')}>
      {children}
    </button>
  ) : (
    <Link to={`/app/admin/organization/users/${memberId}`} className={className}>
      {children}
    </Link>
  );
}

/** Five placeholder rows shaped like members while the first page loads. */
export function MembersTableSkeleton() {
  return (
    <div className="divide-y divide-border" aria-hidden>
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="flex h-[52px] items-center gap-4 px-4">
          <Skeleton className="h-[30px] w-[30px] rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className={cn('h-3.5', row % 2 ? 'w-32' : 'w-44')} />
            <Skeleton className="h-3 w-48" />
          </div>
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-5 w-16" />
        </div>
      ))}
    </div>
  );
}

interface RowMenuProps {
  member: OrgMember;
  busy: boolean;
  actions: MemberActions;
}

function MemberRowMenu({ member, busy, actions }: RowMenuProps) {
  const { t } = useTranslation();
  const suspended = memberStatus(member) === 'SUSPENDED';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" disabled={busy} aria-label={t('more_options')}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <MoreVertical size={16} />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Not asChild: MemberLink doesn't forward refs/props, which
            would silently drop Radix's select/close handling. */}
        <DropdownMenuItem className="flex items-center gap-2" onClick={() => actions.onView(member)}>
          <Eye size={14} />
          {t('view_details')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {!member.isOwner && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="flex items-center gap-2">
              <Shield size={14} />
              {t('change_role')}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {ASSIGNABLE_ROLES.map((r) => (
                <DropdownMenuItem
                  key={r.value}
                  disabled={member.role === r.value}
                  onClick={() => actions.onChangeRole(member, r.value)}
                >
                  {t(r.labelKey)} {member.role === r.value && t('current')}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuItem onClick={() => actions.onToggleSuspend(member)} className="flex items-center gap-2">
          {suspended ? (
            <>
              <UserCheck size={14} />
              {t('activate')}
            </>
          ) : (
            <>
              <UserX size={14} />
              {t('suspend')}
            </>
          )}
        </DropdownMenuItem>
        {!member.isOwner && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => actions.onRemove(member)}
              className="flex items-center gap-2 text-destructive focus:text-destructive"
            >
              <Trash2 size={14} />
              {t('remove')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MembersTableProps {
  members: OrgMember[];
  /** The member whose action is in flight; their menu shows a spinner. */
  busyId: string | null;
  actions: MemberActions;
  /** Dialog mode: the settings dialog drives drill-down via `?member=` instead of navigating. */
  onSelectMember?: (id: string) => void;
}

/** Member · role · status · last activity, with the per-row actions menu. */
export function MembersTable({ members, busyId, actions, onSelectMember }: MembersTableProps) {
  const { t, i18n } = useTranslation();
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>{t('members_col_member')}</TableHead>
          {/* Wide enough for "Administrator" with the owner chip. */}
          <TableHead className="w-[164px]">{t('role')}</TableHead>
          <TableHead className="w-[136px]">{t('status')}</TableHead>
          <TableHead className="hidden w-[140px] lg:table-cell">{t('last_active')}</TableHead>
          <TableHead className="w-14">
            <span className="sr-only">{t('actions')}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => {
          const status = memberStatus(member);
          const name = member.user?.name;
          const contact = member.user?.email || member.user?.phone;
          return (
            <TableRow key={member.id}>
              <TableCell className="max-w-0 py-2">
                {/* w-full: in dialog mode this is a button, which would otherwise size to the
                    name and run into the role column instead of truncating. */}
                <MemberLink memberId={member.id} onSelectMember={onSelectMember} className="group flex w-full min-w-0 items-center gap-2.5">
                  <Avatar name={name} src={member.user?.avatarUrl} size="sm" className="h-[30px] w-[30px]" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium leading-[18px] text-foreground [.group:hover_&]:underline">
                      {name || contact || '—'}
                    </span>
                    {name && contact && (
                      <span className="mt-0.5 block truncate text-[12.5px] leading-4 text-muted-foreground">{contact}</span>
                    )}
                  </span>
                </MemberLink>
              </TableCell>
              <TableCell>
                <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-foreground/80">
                  <span className="truncate">{roleLabel(member.role, t)}</span>
                  {member.isOwner && (
                    <span className="inline-flex h-[18px] shrink-0 items-center rounded-[5px] border border-border px-1.5 text-[11px] font-medium text-foreground/70">
                      {t('group_role_owner')}
                    </span>
                  )}
                </span>
              </TableCell>
              <TableCell>
                <StatusBadge tone={STATUS_TONE[status] ?? 'neutral'}>{statusLabel(status, t)}</StatusBadge>
              </TableCell>
              <TableCell className="hidden truncate text-[13px] text-muted-foreground lg:table-cell">
                {lastActiveLabel(member, t, i18n.language)}
              </TableCell>
              <TableCell className="py-0 text-right">
                <MemberRowMenu member={member} busy={busyId === member.id} actions={actions} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
